import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Server, CheckCircle, AlertTriangle, Activity } from 'lucide-react';
import Header from '../components/Layout/Header';
import StatusPill from '../components/StatusPill';
import { monitorApi } from '../services/api';
import { APP_CONFIG, formatRelative, formatUptime, ENVIRONMENT_TAGS, BU_TAGS } from '../config/appConfig';
import { Monitor, Summary } from '../types';

type TimeframeKey = '24h' | '7d' | '30d' | '90d';
type StatusFilterKey = 'up' | 'down' | null;
type ActiveCardKey = 'total' | 'up' | 'down' | 'uptime' | null;

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, icon, color, active = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`bg-white rounded-xl border p-5 flex items-center gap-4 text-left w-full transition-colors ${
      active ? 'border-brand ring-2 ring-brand/20' : 'border-slate-200'
    } ${onClick ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
  >
    <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
      {icon}
    </div>
    <div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  </button>
);

const MonitorRow: React.FC<{ monitor: Monitor; timeframe: TimeframeKey }> = ({ monitor, timeframe }) => (
  <Link
    to={`/monitor/${monitor.id}`}
    className="flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
  >
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-800 truncate">{monitor.name}</span>
        {monitor.tags && monitor.tags.split(',').filter(Boolean).map(tag => (
          <span key={tag} className="text-[11px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{tag.trim()}</span>
        ))}
      </div>
      <div className="text-xs text-slate-400 truncate mt-0.5">
        <code>{monitor.method}</code> {monitor.url}
      </div>
    </div>
    <div className="hidden sm:block text-right w-24">
      <div className="text-sm font-semibold text-slate-700">
        {formatUptime(
          timeframe === '24h'
            ? monitor.uptime_24h
            : timeframe === '7d'
              ? monitor.uptime_7d
              : timeframe === '30d'
                ? monitor.uptime_30d
                : monitor.uptime_90d
        )}
      </div>
      <div className="text-xs text-slate-400">{timeframe} uptime</div>
    </div>
    <div className="w-32 text-right ml-4">
      <StatusPill status={monitor.current_status} size="sm" />
      <div className="text-xs text-slate-400 mt-1">{formatRelative(monitor.last_checked_at)}</div>
    </div>
  </Link>
);

const DashboardPage: React.FC = () => {
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>(['Production']);
  const [timeframe, setTimeframe] = useState<TimeframeKey>('24h');
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>(null);
  const [activeCard, setActiveCard] = useState<ActiveCardKey>(null);
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
  const tagMenuRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.all([monitorApi.getMonitors(), monitorApi.getSummary()]);
      setMonitors(mRes.data);
      setSummary(sRes.data);
    } catch { /* keep last known good state */ }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, APP_CONFIG.REFRESH_INTERVAL);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!tagMenuRef.current) return;
      if (!tagMenuRef.current.contains(event.target as Node)) {
        setIsTagMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const discoveredTags = Array.from(new Set(
    monitors
      .flatMap(m => (m.tags || '').split(',').map(t => t.trim()).filter(Boolean))
  )).sort((a, b) => a.localeCompare(b));

  const tagFilterOptions = Array.from(new Set([
    ...ENVIRONMENT_TAGS,
    ...BU_TAGS,
    ...discoveredTags,
  ]));

  const tagFilteredMonitors = monitors.filter(m => {
    if (selectedTags.length === 0) return true;
    const tags = (m.tags || '').split(',').map(t => t.trim()).filter(Boolean);
    return selectedTags.some(tag => tags.includes(tag));
  });

  const filteredMonitors = tagFilteredMonitors.filter(m => {
    if (statusFilter === null) return true;
    return m.current_status === statusFilter;
  });

  const selectedTagLabel = selectedTags.length === 0
    ? 'All tags'
    : selectedTags.length === 1
      ? selectedTags[0]
      : `${selectedTags.length} tags`;

  const getUptimeForTimeframe = (m: Monitor): number | null => {
    if (timeframe === '24h') return m.uptime_24h;
    if (timeframe === '7d') return m.uptime_7d;
    if (timeframe === '30d') return m.uptime_30d;
    return m.uptime_90d;
  };

  const tagFilteredSummary = tagFilteredMonitors.reduce((acc, m) => {
    acc.total += 1;
    if (m.current_status === 'up') acc.up += 1;
    else if (m.current_status === 'down') acc.down += 1;
    else if (m.current_status === 'pending') acc.pending += 1;
    else if (m.current_status === 'paused') acc.paused += 1;
    const uptime = getUptimeForTimeframe(m);
    if (uptime != null && Number.isFinite(uptime)) {
      acc.uptimeSum += uptime;
      acc.uptimeCount += 1;
    }
    return acc;
  }, { total: 0, up: 0, down: 0, pending: 0, paused: 0, uptimeSum: 0, uptimeCount: 0 });

  const filteredOverallUptime = tagFilteredSummary.uptimeCount
    ? tagFilteredSummary.uptimeSum / tagFilteredSummary.uptimeCount
    : null;

  // Group monitors by their group name (ungrouped last).
  const grouped = filteredMonitors.reduce<Record<string, Monitor[]>>((acc, m) => {
    const key = m.group_name || 'Ungrouped';
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});
  const groupNames = Object.keys(grouped).sort((a, b) => (a === 'Ungrouped' ? 1 : b === 'Ungrouped' ? -1 : a.localeCompare(b)));

  const groupStats = groupNames.reduce<Record<string, {
    total: number;
    up: number;
    down: number;
    paused: number;
    pending: number;
    uptimeWindow: number | null;
  }>>((acc, name) => {
    const list = grouped[name];
    const total = list.length;
    const up = list.filter(m => m.current_status === 'up').length;
    const down = list.filter(m => m.current_status === 'down').length;
    const paused = list.filter(m => m.current_status === 'paused').length;
    const pending = list.filter(m => m.current_status === 'pending').length;
    const uptimeValues = list
      .map(m => Number(getUptimeForTimeframe(m)))
      .filter(v => Number.isFinite(v));
    const uptimeWindow = uptimeValues.length
      ? uptimeValues.reduce((sum, v) => sum + v, 0) / uptimeValues.length
      : null;

    acc[name] = { total, up, down, paused, pending, uptimeWindow };
    return acc;
  }, {});

  const downByGroup = tagFilteredMonitors.reduce<Record<string, number>>((acc, m) => {
    if (m.current_status !== 'down') return acc;
    const key = m.group_name || 'Ungrouped';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const downGroupNames = Object.keys(downByGroup).sort((a, b) => a.localeCompare(b));
  const overallColor = tagFilteredSummary.down > 0 ? '#DC2626' : '#16A34A';
  const overallLabel = (() => {
    if (tagFilteredSummary.down === 0) return 'All monitors operational';
    if (downGroupNames.length === 1) {
      return `${tagFilteredSummary.down} monitor${tagFilteredSummary.down > 1 ? 's' : ''} down in ${downGroupNames[0]}`;
    }
    return `${tagFilteredSummary.down} monitors down across ${downGroupNames.join(', ')}`;
  })();

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Service Status</h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: overallColor }} />
              {overallLabel}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={tagMenuRef}>
              <button
                type="button"
                onClick={() => setIsTagMenuOpen(open => !open)}
                className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white text-slate-700 min-w-[170px] text-left"
              >
                Tags: {selectedTagLabel}
              </button>
              {isTagMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-20">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Select tags</span>
                    <button
                      type="button"
                      onClick={() => setSelectedTags([])}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      All
                    </button>
                  </div>
                  <div className="max-h-56 overflow-auto p-2 space-y-1">
                    {tagFilterOptions.map(tag => {
                      const checked = selectedTags.includes(tag);
                      return (
                        <label key={tag} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 text-sm text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setSelectedTags(prev => (
                                prev.includes(tag)
                                  ? prev.filter(t => t !== tag)
                                  : [...prev, tag]
                              ));
                            }}
                          />
                          <span>{tag}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setSelectedTags(['Production'])}
                      className="text-xs text-brand hover:opacity-80"
                    >
                      Production
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsTagMenuOpen(false)}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value as TimeframeKey)}
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700"
            >
              <option value="24h">24 hours</option>
              <option value="7d">7 days</option>
              <option value="30d">30 days</option>
              <option value="90d">90 days</option>
            </select>
            <button
              onClick={() => { setRefreshing(true); load(); }}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <SummaryCard
              label="Total Monitors"
              value={tagFilteredSummary.total}
              icon={<Server size={20} />}
              color="#00338D"
              active={activeCard === 'total'}
              onClick={() => {
                setStatusFilter(null);
                setActiveCard('total');
              }}
            />
            <SummaryCard
              label="Operational"
              value={tagFilteredSummary.up}
              icon={<CheckCircle size={20} />}
              color="#16A34A"
              active={activeCard === 'up'}
              onClick={() => {
                if (activeCard === 'up') {
                  setStatusFilter(null);
                  setActiveCard(null);
                  return;
                }
                setStatusFilter('up');
                setActiveCard('up');
              }}
            />
            <SummaryCard
              label="Down"
              value={tagFilteredSummary.down}
              icon={<AlertTriangle size={20} />}
              color="#DC2626"
              active={activeCard === 'down'}
              onClick={() => {
                if (activeCard === 'down') {
                  setStatusFilter(null);
                  setActiveCard(null);
                  return;
                }
                setStatusFilter('down');
                setActiveCard('down');
              }}
            />
            <SummaryCard
              label={`Overall Uptime (${timeframe})`}
              value={formatUptime(filteredOverallUptime)}
              icon={<Activity size={20} />}
              color="#00338D"
              active={activeCard === 'uptime'}
              onClick={() => {
                setStatusFilter(null);
                setActiveCard('uptime');
              }}
            />
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading monitors…</div>
        ) : filteredMonitors.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            No monitors match the current filter. Try selecting different tags/status or head to the <Link to="/admin" className="text-brand font-medium">Admin panel</Link> to add one.
          </div>
        ) : (
          <div className="space-y-6">
            {groupNames.map(name => (
              <div key={name} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 bg-slate-200 border-b border-slate-300 border-l-4 border-l-brand flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{name}</span>
                  <span className="text-xs text-slate-400">{groupStats[name].total} monitor{groupStats[name].total > 1 ? 's' : ''}</span>
                </div>
                <div className="px-5 py-3 border-b border-slate-100">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="rounded-lg border border-slate-200 px-3 py-2">
                      <div className="text-xs text-slate-500">Total</div>
                      <div className="text-sm font-semibold text-slate-700">{groupStats[name].total}</div>
                    </div>
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2">
                      <div className="text-xs text-emerald-700">Up</div>
                      <div className="text-sm font-semibold text-emerald-700">{groupStats[name].up}</div>
                    </div>
                    <div className="rounded-lg border border-rose-100 bg-rose-50/40 px-3 py-2">
                      <div className="text-xs text-rose-700">Down</div>
                      <div className="text-sm font-semibold text-rose-700">{groupStats[name].down}</div>
                    </div>
                    <div className="rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2">
                      <div className="text-xs text-amber-700">Paused</div>
                      <div className="text-sm font-semibold text-amber-700">{groupStats[name].paused + groupStats[name].pending}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 px-3 py-2">
                      <div className="text-xs text-slate-500">{timeframe} Uptime</div>
                      <div className="text-sm font-semibold text-slate-700">{formatUptime(groupStats[name].uptimeWindow)}</div>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {grouped[name].map(m => <MonitorRow key={m.id} monitor={m} timeframe={timeframe} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
